import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WizardService } from 'src/app/wizard-bar/services/wizard.service';
import { forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { QuotingModuleActionTypes, SetQuote } from '@quoting/state/actions/quoting-module.actions';
import { Store } from '@ngrx/store';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { QuoteRequestState } from '@app/shared';
import { QuoteService } from '@app/quoting/services/quote.service';
import { UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { PricingSource, PricingSourceService } from '@app/shared/services/pricing-source.service';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import { QuoteRequestSummary } from '@app/quoting/models/quote-request-summary';
import { UtilityInformationService } from '@app/shared/services/utility-information.service';
import { UdFile } from '@app/filemanagement/UdFile';
import { convertContractToMarginTerm, convertPriceToMarginTerm, removeDuplicatePrimitives } from '@app/shared/helpers/array-helper';
import { ContractLength } from '@app/shared/models/contract-length';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { Offer } from '@app/quoting/models/quote';
import { CustomService } from '@app/custom/services/custom.service';
import { AppState } from '@app/state/app.state';
import { loggedInUtilityHubIdentitySelector, quoteSelector } from '@app/state/app.selectors';
import {
  SetSweetSpotCheckBoxState,
  SetPageName,
  SetProductType,
  SetCommodityType,
  SetContractLengthsSelected,
  SetMarginTerm,
  SetQuoteRequestType,
  SetPricesFilters,
  SetGetQuoteContractLengthStartingButtons,
  SetShowShareQuoteButton,
  SetUtilityUOM,
  SetSweetSpotWasRequested,
} from '@utility-information/state/actions/utility-information.actions';
import { QuoteRequestStatus } from '@app/shared/models/quote-request-status';
import { SetOriginalQuoteRequestId } from '@app/renewals/state/actions/renewals-module.actions';
import { originalQuoteRequestIdSelector } from '@app/renewals/state/selectors/renewals-module.selectors';

@Component({
  selector: 'app-renewals-custom-get-quote',
  templateUrl: './renewals-custom-get-quote.component.html',
  styleUrls: ['./renewals-custom-get-quote.component.scss'],
})
export class RenewalsCustomGetQuoteComponent implements OnInit, OnDestroy {
  public quoteRequestId: number;
  public generatingLoadProfileData: boolean;
  public submitSubject: Subject<void> = new Subject<void>();
  storeQuoteRequestId: number;
  quoteRequestState: QuoteRequestState;
  quote: any;
  public quoteRequestSummary: QuoteRequestSummary;

  @Input() pricingMethod: string;

  uhIdentity$: Subscription;
  route$: Subscription;
  load$: Subscription;
  selectQuote$: Subscription;
  loadProfile$: any;
  user: UtilityHubIdentity;
  file: UdFile;
  rateCodes: string[];
  originalQuoteRequestIdSelector$: Subscription;
  originalQuoteRequestId: number | null;

  constructor(
    private quoteRequestService: QuoteRequestService,
    private route: ActivatedRoute,
    private wizardService: WizardService,
    private store: Store<AppState>,
    private quoteService: QuoteService,
    private quoteCustomService: CustomService,
    private pricingSourceService: PricingSourceService,
    private utilityInformationService: UtilityInformationService
  ) {}

  ngOnDestroy() {
    this.uhIdentity$?.unsubscribe();
    this.route$?.unsubscribe();
    this.load$?.unsubscribe();
    this.selectQuote$?.unsubscribe();
    this.loadProfile$?.unsubscribe();
    this.originalQuoteRequestIdSelector$?.unsubscribe();
  }

  ngOnInit(): void {
    this.wizardService.bindStep(this.route, () => this.save());
    this.store.dispatch(new SetSweetSpotCheckBoxState(this.quoteRequestId?.toString(), true));
    this.store.dispatch(new SetPageName(this.quoteRequestId?.toString(), 'getquote'));
    this.route$ = this.route.params.subscribe((x) => {
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
        if (this.quoteRequestId !== this.storeQuoteRequestId) {
          this.load();
        }
      }
    });
    this.originalQuoteRequestIdSelector$ = this.store.select(originalQuoteRequestIdSelector).subscribe((originalQuoteRequestId) => {
      this.originalQuoteRequestId = originalQuoteRequestId;
    });
  }

  private load() {
    this.load$ = forkJoin({
      quoteRequestState: this.quoteRequestService.getQuoteRequestState(this.quoteRequestId),
      quote: this.quoteRequestService.getSavedQuote(this.quoteRequestId),
      meters: this.quoteRequestService.getUtilityAccounts(this.quoteRequestId),
      rateCodes: this.utilityInformationService.getUtilityRateCodes(this.quoteRequestId),
      formData: this.quoteCustomService.getCompanyDetails(this.quoteRequestId),
    }).subscribe((res) => {
      this.store.dispatch(new SetOriginalQuoteRequestId(res.quoteRequestState.renewedFromQuoteId));
      this.store.dispatch(new SetProductType(res.quoteRequestState.quoteRequestId.toString(), res.quote.planType));
      this.store.dispatch(new SetCommodityType(res.quoteRequestState.quoteRequestId.toString(), res.quote.commodity));
      if (this.isSupplierManagerManualEntry)
        this.quoteRequestService.getQuoteRequestSummary(this.quoteRequestId).subscribe((x) => {
          this.quoteRequestSummary = x;
        });

      if (res.quote.prices?.length > 0) {
        this.store.dispatch(
          new SetContractLengthsSelected(
            this.quoteRequestId.toString(),
            res.quote.prices?.map((x) => x.term)
          )
        );
        let marginTerm = convertPriceToMarginTerm(res.quote.prices as Offer[]);
        this.store.dispatch(new SetMarginTerm(this.quoteRequestId.toString(), marginTerm));
      } else {
        this.store.dispatch(
          new SetContractLengthsSelected(
            this.quoteRequestId.toString(),
            res.formData.contractLengths.map((x) => ({ months: x, selected: true } as ContractLength))
          )
        );
        let marginTerm = convertContractToMarginTerm(
          res.formData.contractLengths.map((x) => ({ months: x, selected: true } as ContractLength)),
          res.quote.marginDollar
        );
        this.store.dispatch(new SetMarginTerm(this.quoteRequestId.toString(), marginTerm));
      }

      this.store
        .select(loggedInUtilityHubIdentitySelector)
        .subscribe((user) => {
          this.user = user;
          this.store.dispatch(new SetQuote(res.quote, user, res.quoteRequestState.hasIDontKnows));
          this.store.dispatch(new SetQuoteRequestType(this.quoteRequestId.toString(), QuoteRequestType.Custom));
          this.store.dispatch(
            new SetPricesFilters(
              this.quoteRequestId.toString(),
              removeDuplicatePrimitives([...res.quote?.prices?.map((price) => price?.planType), 'Show All']),
              res.quote?.prices.length > 0 ? res.quote?.prices?.map((price) => price.term) : res.formData.contractLengths,
              true
            )
          );
          this.store.dispatch(new SetCommodityType(this.quoteRequestId.toString(), res.quoteRequestState.commodity));
          this.store.dispatch(new SetPageName(this.quoteRequestId.toString(), 'getquote'));
          this.store.dispatch(new SetSweetSpotCheckBoxState(this.quoteRequestId.toString(), true));
          this.store.dispatch(
            new SetGetQuoteContractLengthStartingButtons(
              this.quoteRequestId.toString(),
              res.quote.prices?.map((x) => ({ months: x.term, selected: true } as ContractLength))
            )
          );
          this.store.dispatch(new SetProductType(this.quoteRequestId.toString(), res.quote.productType));
          this.store.dispatch(new SetShowShareQuoteButton(true));
        })
        .unsubscribe();
      this.rateCodes = res.rateCodes.map((x) => x.rateCode);
      this.file = res.meters[0].consumptionData.dataFile;
      this.quoteRequestState = res.quoteRequestState;
      this.selectQuote$ = this.store.select(quoteSelector).subscribe((x) => {
        this.quote = x;

        if (x.quote.quoteRequestState?.status == QuoteRequestStatus.QuoteRequested && x.quote?.prices?.length > 0) {
          this.wizardService.stepClicked(this.wizardService.currentStep, false);
        }
      });
      this.quoteRequestId = this.quoteRequestState.quoteRequestId;
      this.quoteService.quoteRequestStateUpdated.emit(res.quoteRequestState);
      let quote = res.quote as any;
      const offersWithNoResult = quote.prices?.filter((x) => x.noResult);
      const offersWithResults = quote.prices?.filter((x) => !x.noResult).sort((a, b) => this.quoteRequestService.sortSellingPrice(a, b));
      const sortedOffers = offersWithResults.concat(offersWithNoResult);
      quote = {
        ...quote,
        prices: offersWithResults,
        marginDollar: this.isGasQuote() ? quote.marginDollar / 100 : quote.marginDollar,
        upliftDollar: this.isGasQuote() ? quote.upliftDollar / 100 : quote.upliftDollar,
      };
      this.quoteService.quoteUpdated.emit(quote);
      this.store.dispatch(new SetUtilityUOM(this.quoteRequestId.toString(), quote.uom));
      this.store.dispatch(new SetSweetSpotWasRequested(this.quoteRequestId.toString(), res?.quoteRequestState?.sweetSpotCheckbox));

      this.uhIdentity$ = this.store.select(loggedInUtilityHubIdentitySelector).subscribe((user) => {
        this.quote = quote;
      });
    });
  }

  public save(): Observable<any> {
    return of(true);
  }

  public next() {
    this.wizardService.next();
  }

  public advanceToLastStep() {
    this.wizardService.advanceToLastStep();
  }

  public back() {
    this.wizardService.previous();
  }

  // Submits a manually entered quote
  public submitQuote() {
    this.submitSubject.next();
  }

  isGasQuote(): boolean {
    return this.quoteRequestState && this.quoteRequestState.commodity == CommodityType.Gas;
  }

  public onAcceptOrRejectOffer(actionTaken: QuotingModuleActionTypes) {
    // TODO this logic needs to move out into the get steps call on the backend where we can disable and enable active steps
    // We only want the user to proceed to the contract details screen next in the journey if they have accepted an offer, otherwise skip to the you're done page.
    if (actionTaken == QuotingModuleActionTypes.OfferAccept) {
      this.wizardService.next();
    } else {
      const youreDoneStep = this.wizardService.activeSteps.find((step) => step.slug === 'youre-done');
      this.wizardService.stepClicked(youreDoneStep, false);
    }
  }

  public isSupplierManagerManualEntry(): Observable<boolean> {
    return new Observable<boolean>((n) => {
      if (!this.quoteRequestState) {
        n.next(false);
      } else {
        this.pricingSourceService
          .getPricingSource(this.quoteRequestState.quoteRequestType, this.quoteRequestState.commodity)
          .subscribe((ps) => {
            n.next(
              this.user.isSupplierManager &&
                this.quoteRequestState.quoteRequestType === QuoteRequestType.Custom &&
                ps.source != PricingSource.Dynamic
            );
            n.complete();
          });
      }
    });
  }
}
